import styled from "styled-components";
import ButtonComponent from "../Button";

const MAX_CONTAINER_WIDTH = "1024px";
import {
  ImagePosition,
  CustomDirection,
  ImagePositionMobile,
  imagePositionMobileBreakpoint,
  ButtonType,
  CustomTextAlign,
  ParagraphType
} from "./types";
interface ContainerProps {
  imagePosition: ImagePosition;
  direction: CustomDirection;
  imagePositionFixed?: boolean;
  imagePositionMobile?: ImagePosition;
  imagePositionMobileBreakpoint?: imagePositionMobileBreakpoint;
  sectionType?: string;
  lastSection?: boolean;
  hasBorder?: boolean;
  alignContent?: string;
}

interface TextButtonContainerProps {
  customTextWidth?: string;
  customDirection: CustomDirection;
  customTextAlign: CustomTextAlign;
  imagePositionFixed?: boolean;
  hasBorder?: boolean;
  imagePositionMobileBreakpoint?: imagePositionMobileBreakpoint;
  imagePositionMobile?: ImagePosition;
}

interface TitleProps {
  customTitleColor?: string;
  customTitleFontSize?: string;
  customTitleFontSizeMobile?: string;
  customTitleLineHeight?: string;
  titleMaxWidth?: string;
  titleWidth?: string;
  sectionType?: string;
  customTitleLetterSpacing?: string;
}

interface HighlightWordProps {
  highlightWordColor?: string;
}

interface ParagraphProps {
  maxWidth?: string;
  sectionType?: string;
  type?: ParagraphType;
  customFontSize?: string;
  customFontFamily?: string;
}

interface ButtonProps {
  buttonType: ButtonType;
  margin?: string;
  customArrowColor?: string;
}

interface ImageContainerProps {
  imagePosition: ImagePosition;
  imagePositionFixed?: boolean;
  imagePositionMobile?: ImagePositionMobile; 
  imageCustomAspectRatio?: string;
  imageCustomWidth?: string;
  imageCustomHeight?: string;
  imageWidth2xl?: string;
  imageAspectRatio2xl?: string;
  imageWidthxl?: string;
  imageAspectRatioXl?: string;
  imageWidthlg?: string;
  imageAspectRatiolg?: string;
  imageWidthmd?: string;
  imageAspectRatiomd?: string;
  imageOffsetOnMobile?: boolean;
  scaleImageOnMobile?: boolean;
  imageWidthsm?: string;
  imageAspectRatiosm?: string;
  imageContainerMaxWidth?: string;
}

const _handleImagePositionMobile = (props: ContainerProps) => {
  switch( props.imagePositionMobile ) {
    case 'top':
      return 'column-reverse';
    case 'bottom':
      return 'column';
    case 'left':
      return 'row-reverse';
    case 'right':
      return 'row';
    default:
      return 'column';
  }
}

const _handleImageWidthMobile = (props: ContainerProps) => {
  switch( props.imagePositionMobile ) {
    case 'top':
      return 'width: 100%; max-width: 100%';
    case 'bottom':
      return 'width: 100%; max-width: 100%';
    case 'left':
      return '';
    case 'right':
      return '';
    default:
      return '100%';
  }
}

export const Container = styled.div<ContainerProps>`
  padding: 96px 48px 48px 48px; 
  margin: auto;
  
  width: 100%;
  max-width: ${MAX_CONTAINER_WIDTH};
  
  position: relative;
  display: flex;
  align-items: ${props => props.alignContent || "center"};
  justify-content: space-between;
  flex-direction: row;
  
  ${ props => props.hasBorder && `
    padding: 64px 48px 64px 48px;
  `}

  ${props => props.imagePosition === "left" && `
    flex-direction: row-reverse;
    align-items: center;
  `}

  ${props => props.direction === "column" && `  
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin: auto;
  `}

  ${ props => props.imagePosition === "top" && `
    flex-direction: column-reverse;
    align-items: center;
    justify-content: center;
    gap: 64px;
  `}

  ${props => props.imagePosition === "bottom" && `
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 64px;
  `}

  ${ props => props.imagePositionFixed && `
    overflow: hidden;
  `}

  ${props => props.sectionType === 'hero' && `
    gap: 81px;
  `}

  ${
    props => props.lastSection && `
      padding-bottom: 64px;
    `
  }

  @media ${(props) => props.theme.breakpoints && props.theme.breakpoints.md} {
    padding-top: 48px;
    padding-bottom: 64px;
    padding-left: clamp(24px, calc(24px + (32 - 24) * ((100vw - 400px) / (768 - 400))), 32px);
    padding-right: clamp(24px, calc(24px + (32 - 24) * ((100vw - 400px) / (768 - 400))), 32px);
    gap: clamp(12px, calc(12px + (64 - 12) * ((100vw - 400px) / (768 - 400))), 32px);

    ${ props => props.imagePositionMobileBreakpoint  === 'md' && `
      flex-direction: ${_handleImagePositionMobile(props)};
      
    `}


    ${props => props.sectionType === 'hero' && `
      gap: 132px;
    `}
  }

  @media ${(props) => props.theme.breakpoints && props.theme.breakpoints.sm} {
    padding: 48px 24px 24px 24px;
    gap: 64px;
    flex-direction: ${props => _handleImagePositionMobile(props)};
  
    ${props => props.sectionType === 'hero' && `
      gap: 67px;
    `}
    
    ${props => props.lastSection && `
      padding-bottom: 64px;
    `}
  } 
`;

export const TextButtonContainer = styled.div<TextButtonContainerProps>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: clamp(24px, calc(24px + (48 - 24) * ((100vw - 786px) / (1024 - 786))), 48px);
    flex-grow: 1;
    z-index: 101;

    ${ props => props.hasBorder && `
      padding: 48px 24px;
      border-radius: 16px;
      border: 1px solid rgba(255, 255, 255, 0.16);
    `}

    max-width: ${(props) => props.customTextWidth ? props.customTextWidth : props.customDirection === "column" ?
       "100%" : "40%"};

    ${ props => props.customTextAlign === "center" && `
      align-items: center;
      text-align: center;
      justify-content: center;
      margin: auto;
    `}

    ${ props => props.customDirection === "column" && `
      align-items: center;
      text-align: center;
    `}

    @media ${(props) => props.theme.breakpoints && props.theme.breakpoints.md} {
        gap: clamp(16px, calc(16px + (24 - 16) * ((100vw - 400px) / (768 - 400))), 24px);

        ${props => props.imagePositionFixed && `
          margin-right: -20%;
        `}

        ${props => props.imagePositionMobileBreakpoint === 'md' && `
          ${_handleImageWidthMobile(props)}
        `}
    }
    
    @media ${(props) => props.theme.breakpoints && props.theme.breakpoints.sm} {
      width: 100%;
      max-width: 100%;
      margin: auto;
      gap: clamp(12px, 1.5vw, 16px);
    }
`;

export const TextContainer = styled.div`
    color: #fff;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: clamp(16px, 1.56vw + 8px, 24px);
    align-self: stretch;
`;

export const Title = styled.label<TitleProps>`
  color: ${(props) => props.customTitleColor || "#fff"};
  align-self: stretch;
  display: inline-block;
  text-shadow: 0px 4px 8px rgba(16, 24, 40, 0.10), 0px 2px 4px rgba(16, 24, 40, 0.06);
  font-family: "everettmedium";
  
  font-size: ${(props) => props.customTitleFontSize || "48px"}; /* fallback */
  font-size: ${(props) => props.customTitleFontSize || "clamp(40px, 3.125vw + 16px, 48px)"};
  line-height: ${(props) => props.customTitleLineHeight || "1.2"};
  letter-spacing: ${(props) => props.customTitleLetterSpacing || "-0.8px"};
  font-weight: 500;
  font-style: normal;
  width: 100%;

  ${(props) => props.sectionType === 'hero' && `
    font-size: ${(props) => props.customTitleFontSize || "72px"}; /* fallback */
    font-size: ${(props) => props.customTitleFontSize || "clamp(56px, 6.25vw + 8px, 72px)"};
    line-height: ${(props) => props.customTitleLineHeight || "1.1"};
  `}

  @media ${(props) => props.theme.breakpoints && props.theme.breakpoints.lg } {
    max-width: 100%;
  }

  @media ${(props) => props.theme.breakpoints && props.theme.breakpoints.md} {
    font-size:${(props) => props.customTitleFontSizeMobile || "40px"}; /* fallback */
    font-size: ${(props) => props.customTitleFontSizeMobile || "clamp(32px, 2.37vw + 21.8px, 40px)"};
    line-height: 1.2;
    letter-spacing: -0.4px;

    ${ props => props.sectionType === 'hero' && `
      font-size: clamp(48px, 3.125vw + 24px, 56px);
      line-height: 1.14;
    `}
  }

  @media ${(props) => props.theme.breakpoints && props.theme.breakpoints.sm} {
    font-size: 32px; /* fallback */
    font-size: ${(props) => props.customTitleFontSizeMobile || "clamp(28px, 3vw, 32px)"};
    line-height: 1.25;

    ${ props => props.sectionType === 'hero' && `
      font-size: 48px; /* fallback */
      font-size: clamp(40px, 3vw, 48px);
      line-height: 1.17;
    `}
  }
`;

export const HighlightWord = styled.label<HighlightWordProps>`    
  color: ${(props) => props.highlightWordColor || "#fff"};
  font-family: "everettmedium";
`;

export const DescriptionsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

export const Paragraph = styled.p<ParagraphProps>`
  width: 100%;
  max-width: ${(props) => props.maxWidth || "auto"};
  font-style: normal;
  font-family: ${(props) => props.customFontFamily || "everettmedium"};
  font-weight: 400;
  color: ${props => props.color || "rgba(255, 255, 255, 0.96)"};
  font-size: ${(props) => props.customFontSize || "18px"}; /* fallback */
  font-size: ${(props) => props.customFontSize || "clamp(16px, 0.78vw + 10px, 18px)"};
  line-height: 1.55;

  ${props => props.sectionType === 'hero' && `
    font-size: ${(props) => props.customFontSize || "24px"}; /* fallback */
    font-size: ${(props) => props.customFontSize || "clamp(18px, 2.34vw + 0px, 24px)"};
    line-height: 1.67;
    padding-left: clamp(24px, 21.875vw - 144px, 80px);
    padding-right: clamp(24px, 21.875vw - 144px, 80px);
  `}

  ${props => props.type === 'title' && `
    font-weight: 600;
    font-size: ${(props) => props.customFontSize || "clamp(18px, 0.78vw + 12px, 20px)"};
    line-height: 1.6;
    text-align: center;
  `}

  ${props => props.type === 'list' && `
    font-weight: 400;
    font-size: clamp(18px, 0.78vw + 12px, 20px);
    line-height: 1.6;
    text-align: center;
  `}

  @media ${(props) => props.theme.breakpoints && props.theme.breakpoints.md} {
    ${props => props.sectionType === 'hero' && `
      padding-left: 48px; /* fallback */
      padding-right: 48px; /* fallback */
      padding-left: clamp(24px, 6.52vw + -2.08px, 48px);
      padding-right: clamp(24px, 6.52vw + -2.08px, 48px);
    `}

    ${props => (props.type === 'list' || props.type === 'title') && `
      font-size: 18px;
      line-height: 1.6;
    `}
  }

  @media ${(props) => props.theme.breakpoints && props.theme.breakpoints.sm} {
    font-size: 16px;
    line-height: 1.5;

    ${props => props.sectionType === 'hero' && `
      font-size: 20px;
      line-height: 1.6;
      padding-left: 0;
      padding-right: 0;
    `}
  }
`;

export const ButtonsSection = styled.section`
  display: flex;

  @media ${(props) => props.theme.breakpoints && props.theme.breakpoints.tablet} {
    justify-content: space-around;
  }

  @media ${(props) => props.theme.breakpoints && props.theme.breakpoints.mobile} {
    flex-direction: column;
    align-items: center;
    align-items: stretch;

  @media ${(props) => props.theme.breakpoints && props.theme.breakpoints.sm} {
    flex-direction: column;
    align-items: center;
    width: 100%;

    a {
      width: 100%;
    }
  }
`;

export const Button = styled(ButtonComponent)<ButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 32px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  white-space: nowrap;
  font-family: "Inter";
  font-weight: 500;

  border-radius: 48px;

  ${props => props.buttonType === 'primary' && `
    border: 2px solid ${props.theme.buttonPrimaryBorderColor};
    background: ${props.theme.buttonPrimaryColor};
    hoverColor: ${props.theme.buttonPrimaryHoverColor};
    box-shadow: 0px 10px 15px -3px rgba(0, 0, 0, 0.10), 0px 4px 6px -2px rgba(0, 0, 0, 0.05);
    ` 
  }

  ${props => props.buttonType === 'secondary' && `
        background-color: ${props.theme.buttonSecondaryColor};
        border: 2px solid ${props.theme.buttonSecondaryBorderColor};
        hoverColor: ${props.theme.buttonSecondaryHoverColor};
        box-shadow: 0px 10px 15px -3px rgba(29, 29, 29, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05);
    `
  }

  ${props => props.buttonType === 'pacaswap' && `
    border-radius: 48px;
    border: 1.5px solid rgba(255, 255, 255, 0.24);
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.16) 0%, rgba(0, 0, 0, 0.16) 100%), linear-gradient(90deg, #5A35E9 0%, #7B36F2 51%, #BC30D7 100%);
    box-shadow: 0px 10px 15px -3px rgba(0, 0, 0, 0.08), 0px 4px 6px -2px rgba(0, 0, 0, 0.04);
  `}

  ${props => props.buttonType === 'pacaswap_secondary' && `
    background: ${props.theme.buttonSecondaryColor};
    hoverColor: #BA30D7;
    border-radius: 48px;
    border: 2px solid #BA30D7;
    box-shadow: 0px 10px 15px -3px rgba(0, 0, 0, 0.10), 0px 4px 6px -2px rgba(0, 0, 0, 0.05);
  `}

  margin: ${( props ) => props.margin || "0px"};

  svg {
    margin-left: 8px;
    width: 20px;
    height: 20px;
    
    @media ${(props) => props.theme.breakpoints && props.theme.breakpoints.tablet} {
      width: 15px;
      height: 15px;
    }

    path {
      fill: ${(props) => props.customArrowColor || "#FFF"};
    }
  }

  @media ${(props) => props.theme.breakpoints && props.theme.breakpoints.md} {
    font-size: 0px;
    line-height: 28px;
    font-size: 18px;
    width: 100%;
    height: 60px;
    margin: 20px 0px 0px;
  }

  @media ${(props) => props.theme.breakpoints && props.theme.breakpoints.sm} {
    font-size: 16px;
    line-height: 24px;
  }
`;

export const ButtonGroupContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 14px;

  button {
    padding: 14px 24px; 
    font-size: 20px;
  }

  @media ${(props) => props.theme.breakpoints && props.theme.breakpoints.tablet} {
    button {
      height: 48px !important;
    }
  }

  @media ${(props) => props.theme.breakpoints && props.theme.breakpoints.md} {
    align-items: flex-start;
    gap: 8px;

    button {
      padding: 12px 20px;
      font-size: 16px;
    }
  }

  @media ${(props) => props.theme.breakpoints && props.theme.breakpoints.sm} {
    width: 100%;
    flex-direction: column;
    align-items: center;
    gap: 0px;
  }
`;

export const ImageContainer = styled.div<ImageContainerProps>`
  display: flex;
  margin-left: auto;
  top: 0;
  position: relative;
  justify-content: flex-start;
  z-index: 100;
  flex: 0 1 50%;

  ${props => props.imagePosition === "left" && `
    margin-left: 0;
    justify-content: flex-start;
  `}

  ${props => props.imagePosition === "bottom" && `
    justify-content: center;
  `}

  ${props => props.imageContainerMaxWidth && `
    flex: 0 1 ${props.imageContainerMaxWidth};
  `}

  @media ${(props) => props.theme.breakpoints && props.theme.breakpoints.md} {
    ${props => props.imageOffsetOnMobile && `
      width: auto;
    `}
  }

  @media ${(props) => props.theme.breakpoints && props.theme.breakpoints.sm} {
    justify-content: center;

    ${props => props.imagePositionFixed && `
        width: auto;
        flex: 0 0 100%;
    `}
  }

  img {
    display: block;
    aspect-ratio: ${ (props) => (props.imageCustomAspectRatio ? props.imageCustomAspectRatio : "auto")};
    width: ${(props) => (props.imageCustomWidth ? props.imageCustomWidth : "100%")};
    height: ${(props) => ( props.imageCustomHeight ? props.imageCustomHeight : "auto")};
    max-width: ${(props) => (props.imageCustomWidth ? props.imageCustomWidth : "100%")}; 
    object-fit: contain;

    ${props => props.imagePositionFixed && `
          max-width: unset;
          position: absolute;
          top: 0;
          left: 0;
    `}

    ${props => props.imageWidth2xl && ` 
        max-width: ${props.imageWidth2xl};
        aspect-ratio: ${props.imageAspectRatio2xl ? props.imageAspectRatio2xl : "auto"};
    `}

    @media ${(props) => props.theme.breakpoints && props.theme.breakpoints.xl} {
        ${props => props.imageWidthxl && ` 
            max-width: ${props.imageWidthxl};
            aspect-ratio: ${props.imageAspectRatioXl ? props.imageAspectRatioXl : "auto"};
        `}
    }

    @media ${(props) => props.theme.breakpoints && props.theme.breakpoints.lg} {
      ${props => props.imageWidthlg && ` 
          max-width: ${props.imageWidthlg};
          aspect-ratio: ${props.imageAspectRatiolg ? props.imageAspectRatiolg : "auto"};
      `}

      width: 100%;

      ${props => props.imagePositionFixed && `
        width: auto;
      `}
    }
    
    @media ${(props) => props.theme.breakpoints && props.theme.breakpoints.md} {
         ${props => props.imageWidthmd && ` 
            max-width: ${props.imageWidthmd};
            aspect-ratio: ${props.imageAspectRatiomd ? props.imageAspectRatiomd : "auto"};
        `}

        ${props => props.scaleImageOnMobile && `
          transform: scale(1);
          top: 0px;
          left: 0px;
          width: 100%;
          height: 100%;
        `}

        ${props => props.imageOffsetOnMobile && props.imageWidthmd && `
          width: ${props.imageWidthmd};
        `}
    }

    @media ${(props) => props.theme.breakpoints && props.theme.breakpoints.sm} {
        ${props => props.imageWidthsm && `  
            max-width: ${props.imageWidthsm};
            aspect-ratio: ${props.imageAspectRatiosm ? props.imageAspectRatiosm : "auto"};
        `}

        ${props => props.imagePositionFixed && `    
            position: relative;
            margin:auto;
        `}
      }
    }
`;
